import { h, type FunctionalComponent } from 'preact';
import { useMemo, useState, useEffect } from 'preact/hooks';
import { memo } from 'preact/compat';
import { useUwBranding, useTranslation } from 'hooks';

import { useWidgetState } from 'hooks/use-widget-state';

import { INACTIVE, COMPONENT_NAME_FEATURE, ARIA_LIVE_VISIBILITY_DELAY_MS } from './constants';
import { FeatureStatus } from './feature-status';
import { FeatureSteps } from './feature-steps';
import { FeatureIcon } from './feature-icon';
import './feature.scss';

import type { FeatureProps } from './types';
import type { AllyFeatureData } from '../use-ally-features';

export const FeatureComponent: FunctionalComponent<FeatureProps & AllyFeatureData> = (props) => {
  const {
    name,
    disabledSlot,
    actionState = INACTIVE,
    actionStatesMeta,
    onClick,
    temporaryDisabled = false,
    children,
  } = props;

  // Track if the widget has fully opened to prevent unwanted VoiceOver announcements
  const [isMenuFullyOpened, setIsMenuFullyOpened] = useState(false);
  const { isOpen } = useWidgetState();

  useEffect(() => {
    if (!isOpen) return;

    setIsMenuFullyOpened(false);
    // Re-enable after focus has settled, should be enough for focus transition)
    setTimeout(() => {
      setIsMenuFullyOpened(true);
    }, ARIA_LIVE_VISIBILITY_DELAY_MS);

    return () => setIsMenuFullyOpened(false);
  }, [isOpen]);

  /** It's confirmed to hide such features for now,
   * but we keep logic of disabling and explanation of reason in this
   * component below (until final decision)
   * */
  if (temporaryDisabled) {
    return null;
  }

  const { translate } = useTranslation();
  const { isWhiteLabeled } = useUwBranding();

  const isFeatureDisabledClass = useMemo(
    () => (temporaryDisabled ? 'ally-action-item__disabled' : ''),
    [temporaryDisabled],
  );

  const isDisabled = useMemo(() => (disabledSlot ? 'disabled' : ''), [disabledSlot]);
  const currentActionStatesMeta = useMemo(
    () => actionStatesMeta[`${actionState}`],
    [actionStatesMeta, actionState],
  );

  const getInnerReaderContent = useMemo(() => {
    if (temporaryDisabled && typeof temporaryDisabled === 'object') {
      return temporaryDisabled.reason;
    }

    if (name === 's9') {
      return translate(
        isWhiteLabeled ? currentActionStatesMeta.aria_wl : currentActionStatesMeta.aria,
      );
    }

    return translate(currentActionStatesMeta.aria || currentActionStatesMeta.label);
  }, [currentActionStatesMeta, isWhiteLabeled, translate, temporaryDisabled, name]);

  const handleClick = useMemo(
    () => (event: MouseEvent) => {
      if (!temporaryDisabled) {
        onClick(event);
      }
    },
    [temporaryDisabled, onClick],
  );

  const featureClassName = useMemo(() => {
    const baseClass = `uwaw-features__item ${isDisabled} ${isFeatureDisabledClass}`;
    const activeClass = actionState !== INACTIVE ? 'uwaw-features__item_active' : '';
    return `${baseClass} ${activeClass}`.replace(/\s+/, ' ').trim();
  }, [isDisabled, isFeatureDisabledClass, actionState]);

  return (
    <div className={featureClassName} data-testid={COMPONENT_NAME_FEATURE}>
      <button
        id={`btn-${name}`}
        className="uwaw-features__item__i"
        onClick={handleClick}
        data-uw-reader-content={getInnerReaderContent}
        aria-label={getInnerReaderContent}
        aria-pressed={!!actionState}
        data-testid={`${COMPONENT_NAME_FEATURE}-button`}
        aria-live={isMenuFullyOpened ? 'assertive' : 'off'}
      >
        <FeatureIcon featureName={name} />
        <FeatureStatus featureName={name} />
        <FeatureSteps featureName={name} />
      </button>
      {/* place for info content */}
      {children}
    </div>
  );
};

export const Feature = memo(FeatureComponent);

FeatureComponent.displayName = COMPONENT_NAME_FEATURE;
